import type { DashboardHealthDto, MemberDto } from "@gladis/shared";
import { formatEur } from "@gladis/shared";
import { StatCard, type StatCardRow } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { filterSummary, type HealthFilterState } from "./health-filters.js";

/**
 * In, Out and Left — three of the four cards the page opens with.
 *
 * ==========================================================================================
 * THREE CARDS WHERE THERE WAS ONE CARD WITH THREE ROWS
 * ==========================================================================================
 *
 * `Left` used to be a headline with In and Out as rows beneath it, because `dashboard-layout.md`
 * required the health card and the net worth card to be visual PEERS and a card with no headline figure
 * is not a peer of one with a large figure.
 *
 * The peer requirement is now met by the row itself: four cards of the same size, the same type scale
 * and the same panel, which is what `StatRow` is for. So the three figures are three cards, each at the
 * weight the others have.
 *
 * WHAT MUST NOT BE LOST IN THE SPLIT is that In, Out and Left are ONE SUBTRACTION. Three cards in a row
 * invite being read as three unrelated readings, and `Left` is not a reading — it is the other two
 * subtracted. So `Left` keeps the percentage, the prompts and the reconciliation caveat that qualify it,
 * and its hint is where the relationship is stated in words.
 *
 * ==========================================================================================
 * AND IN THREE OF THE FOUR STATES A FIGURE IS ABSENT RATHER THAN ZERO
 * ==========================================================================================
 *
 * AC-D16, unchanged by the layout. With no income recorded, `Left` is a deficit equal to the household's
 * entire expense total — an arithmetically correct figure that states something false: it reads as a
 * household spending far beyond its means, when what is true is that nobody has typed in the income
 * yet. So `emptiness` decides which cards carry figures, and a card with no figure to give says what to
 * do instead of showing a zero.
 */

/** Cents-as-string → the euro amount `formatEur` wants. Parsed once, at the point of formatting. */
function euros(cents: string): number {
  return Number(cents) / 100;
}

/** Where the member goes to fix an empty side. Both links, always (FR-056). */
function ModuleLinks(): ReactNode {
  const t = useT();
  return (
    <>
      <Link to="/income" className="underline hover:text-ink">
        {t("nav.income")}
      </Link>
      {" · "}
      <Link to="/expenses" className="underline hover:text-ink">
        {t("nav.expenses")}
      </Link>
    </>
  );
}

export function HealthFigures({
  health,
  members,
  filters,
}: {
  /** Undefined while the first read is in flight, so the cards keep their shape. */
  health: DashboardHealthDto | undefined;
  members: readonly MemberDto[];
  filters: HealthFilterState;
}): ReactNode {
  const t = useT();
  const window = filterSummary(filters, members);

  if (health === undefined) {
    return (
      <>
        <StatCard label={t("dash.health.in")} hint={t("dash.health.reading")} />
        <StatCard label={t("dash.health.out")} hint={t("dash.health.reading")} />
        <StatCard
          label={t("dash.health.left")}
          hint={t("dash.health.reading")}
          testId="health-card"
        />
      </>
    );
  }

  const showsIn = health.emptiness === "none" || health.emptiness === "no-expenses";
  const showsOut = health.emptiness === "none" || health.emptiness === "no-income";
  /*
   * `Left` is shown when both sides have something to say. `no-income` is excluded for AC-D16's reason
   * above; `neither` has nothing at all. This is the same condition the single card expressed by
   * choosing its headline, restated as a question about one card.
   */
  const showsLeft = health.emptiness === "none" || health.emptiness === "no-expenses";

  /*
   * A MUTED ROW ON THE `In` CARD, present only in the per-member scope. Joint income is not divided
   * between members — dividing it would assert a contribution ratio nothing in the application records
   * (AC-D15, savings' FR-S6a) — so it sits beneath the member's own figure as its own fact.
   */
  const inRows: StatCardRow[] =
    health.jointIncomeCents === null
      ? []
      : [
          {
            key: "joint",
            label: t("dash.health.jointNotAttributed"),
            value: formatEur(euros(health.jointIncomeCents)),
            muted: true,
          },
        ];

  return (
    <>
      <StatCard
        label={t("dash.health.in")}
        {...(showsIn ? { value: formatEur(euros(health.inCents)) } : {})}
        rows={inRows}
        hint={showsIn ? window : t("dash.health.noIncome")}
        testId="health-in-card"
        valueTestId="health-in"
      />

      <StatCard
        label={t("dash.health.out")}
        /*
         * NEGATED HERE, and this is the one place that does it. The server sends a positive magnitude
         * precisely so that no value can be negated twice by two owners — the symptom of which is
         * spending shown as income, a perfectly plausible-looking number.
         */
        {...(showsOut ? { value: formatEur(-euros(health.outCents)) } : {})}
        hint={showsOut ? window : t("dash.health.noExpenses")}
        testId="health-out-card"
        valueTestId="health-out"
      />

      <StatCard
        label={t("dash.health.left")}
        {...(showsLeft ? { value: formatEur(euros(health.leftCents)) } : {})}
        hint={<LeftHint health={health} window={window} />}
        // `health-card` STAYS ON THIS CARD. It was the whole health card's id, and this card inherits
        // what that id was used to reach: the headline, the percentage, the prompt, the caveat and the
        // two module links. Renaming it would churn the e2e suite to say the same thing.
        testId="health-card"
        valueTestId="health-headline"
      />
    </>
  );
}

/**
 * What qualifies `Left`: the window, the percentage, the prompt, and the reconciliation caveat.
 *
 * Stacked rather than joined into one sentence, because they answer different questions and a member
 * reading for one of them should not have to parse the others.
 */
function LeftHint({ health, window }: { health: DashboardHealthDto; window: string }): ReactNode {
  const t = useT();
  return (
    <>
      {health.emptiness === "none" ? <span>{window}</span> : null}

      {health.keptPercent === undefined ? null : (
        // Shown only when In is greater than zero (FR-041). A percentage of nothing is not 0%, and the
        // sign is carried in the number rather than in a colour (AC-D23).
        <span data-testid="health-kept" className="mt-1 block">
          {health.keptPercent}% of what came in
        </span>
      )}

      {health.emptiness === "none" ? null : (
        <span data-testid="health-prompt" className="mt-1 block">
          {health.emptiness === "no-income"
            ? t("dash.health.noIncomeToCompare")
            : health.emptiness === "no-expenses"
              ? t("dash.health.noExpensesSoAllLeft")
              : t("dash.health.nothingYet")}
          <ModuleLinks />
        </span>
      )}

      {health.reconciles ? null : (
        // FR-052: STATED, never papered over. Joint income is not divided, and a departed member's
        // stored share still counts towards the household — so the members' figures cannot be expected
        // to add up to the household's, and a card that implied they did would be the wrong fix.
        <span data-testid="health-reconcile" className="mt-1 block">
          {t("dash.health.membersDoNotAddUp")}
        </span>
      )}
    </>
  );
}
